import { createSlice } from '@reduxjs/toolkit';
import {
  fetchCRMPatientsThunk,
  fetchCRMPatientByIdThunk,
  convertLeadToPatientThunk,
  updatePatientProfileThunk,
  addPatientNoteThunk,
  deletePatientNoteThunk,
  deletePatientProfileThunk,
} from '../thunks/crmPatientsThunks';

const FAVORITES_KEY = 'crm.patients.favorites';
const PANEL_WIDTHS_KEY = 'crm.patients.panelWidths';

const loadFavorites = () => {
  try {
    const raw = localStorage.getItem(FAVORITES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const loadPanelWidths = () => {
  try {
    const raw = localStorage.getItem(PANEL_WIDTHS_KEY);
    return raw ? JSON.parse(raw) : { list: 380, messages: 380 };
  } catch {
    return { list: 380, messages: 380 };
  }
};

const persistFavorites = (favs) => {
  try { localStorage.setItem(FAVORITES_KEY, JSON.stringify(favs)); } catch {}
};

const persistPanelWidths = (widths) => {
  try { localStorage.setItem(PANEL_WIDTHS_KEY, JSON.stringify(widths)); } catch {}
};

const initialState = {
  items: [],
  totalCount: 0,
  totalPages: 0,
  loading: false,
  error: null,

  currentPage: 1,
  pageSize: 25,

  filters: {
    search: '',
    status: 'Active',
    tag: '',
    sortBy: 'lastActivity', // lastActivity | name | created
    unreadOnly: false,
  },

  selectedPatientId: null,
  selectedPatient: null,
  selectedPatientLoading: false,
  selectedPatientError: null,

  activeTab: 'overview',
  showDetail: false,
  showMessagesPanel: false,

  selectedIds: [],
  favorites: loadFavorites(),
  panelWidths: loadPanelWidths(),
};

const crmPatientsSlice = createSlice({
  name: 'crmPatients',
  initialState,
  reducers: {
    setPatients: (state, action) => {
      state.items = action.payload;
    },
    updatePatientInList: (state, action) => {
      const { id, changes } = action.payload;
      const index = state.items.findIndex(patient => patient._id === id);
      if (index !== -1) {
        state.items[index] = { ...state.items[index], ...changes };
      }
    },
    removePatientFromList: (state, action) => {
      state.items = state.items.filter(patient => patient._id !== action.payload);
      state.totalCount = Math.max(0, state.totalCount - 1);
    },
    setSearch: (state, action) => {
      state.filters.search = action.payload;
    },
    setStatusFilter: (state, action) => {
      state.filters.status = action.payload;
    },
    setTagFilter: (state, action) => {
      state.filters.tag = action.payload;
    },
    setSortBy: (state, action) => {
      state.filters.sortBy = action.payload;
    },
    setUnreadOnly: (state, action) => {
      state.filters.unreadOnly = action.payload;
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
      state.currentPage = 1;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
    setSelectedPatientId: (state, action) => {
      state.selectedPatientId = action.payload;
    },
    setSelectedPatient: (state, action) => {
      state.selectedPatient = action.payload;
    },
    clearSelectedPatient: (state) => {
      state.selectedPatientId = null;
      state.selectedPatient = null;
      state.selectedPatientError = null;
    },
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
    setShowDetail: (state, action) => {
      state.showDetail = action.payload;
    },
    setShowMessagesPanel: (state, action) => {
      state.showMessagesPanel = action.payload;
    },
    toggleSelectedId: (state, action) => {
      const id = action.payload;
      if (state.selectedIds.includes(id)) {
        state.selectedIds = state.selectedIds.filter(x => x !== id);
      } else {
        state.selectedIds.push(id);
      }
    },
    clearSelectedIds: (state) => {
      state.selectedIds = [];
    },
    selectAllOnPage: (state) => {
      state.selectedIds = state.items.map(p => p._id);
    },
    toggleFavorite: (state, action) => {
      const id = action.payload;
      if (state.favorites.includes(id)) {
        state.favorites = state.favorites.filter(x => x !== id);
      } else {
        state.favorites.push(id);
      }
      persistFavorites(state.favorites);
    },
    setListPanelWidth: (state, action) => {
      state.panelWidths.list = action.payload;
      persistPanelWidths(state.panelWidths);
    },
    setMessagesPanelWidth: (state, action) => {
      state.panelWidths.messages = action.payload;
      persistPanelWidths(state.panelWidths);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCRMPatientsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCRMPatientsThunk.fulfilled, (state, action) => {
        state.items = action.payload.items || [];
        state.totalCount = action.payload.totalCount || 0;
        state.totalPages = action.payload.totalPages || 0;
        state.loading = false;
      })
      .addCase(fetchCRMPatientsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      .addCase(fetchCRMPatientByIdThunk.pending, (state) => {
        state.selectedPatientLoading = true;
        state.selectedPatientError = null;
      })
      .addCase(fetchCRMPatientByIdThunk.fulfilled, (state, action) => {
        state.selectedPatient = action.payload;
        state.selectedPatientLoading = false;
      })
      .addCase(fetchCRMPatientByIdThunk.rejected, (state, action) => {
        state.selectedPatientLoading = false;
        state.selectedPatientError = action.error.message;
      })

      .addCase(convertLeadToPatientThunk.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
        state.totalCount += 1;
      })

      // updatePatientProfileThunk — patches both selectedPatient and list row
      .addCase(updatePatientProfileThunk.fulfilled, (state, action) => {
        const { id, updated } = action.payload || {};
        if (!updated) return;
        if (state.selectedPatient && state.selectedPatient._id === id) {
          state.selectedPatient = { ...state.selectedPatient, ...updated };
        }
        const idx = state.items.findIndex(p => p._id === id);
        if (idx !== -1) {
          state.items[idx] = { ...state.items[idx], ...updated };
        }
      })
      .addCase(addPatientNoteThunk.fulfilled, (state, action) => {
        const { id, updated } = action.payload || {};
        if (!updated) return;
        if (state.selectedPatient && state.selectedPatient._id === id) {
          state.selectedPatient = { ...state.selectedPatient, notes: updated.notes };
        }
      })
      .addCase(deletePatientNoteThunk.fulfilled, (state, action) => {
        const { id, updated } = action.payload || {};
        if (!updated) return;
        if (state.selectedPatient && state.selectedPatient._id === id) {
          state.selectedPatient = { ...state.selectedPatient, notes: updated.notes };
        }
      })

      // deletePatientProfileThunk — drop from list, drop selection, drop checkbox
      .addCase(deletePatientProfileThunk.fulfilled, (state, action) => {
        const id = action.payload?.id;
        if (!id) return;
        state.items = state.items.filter(p => p._id !== id);
        state.totalCount = Math.max(0, state.totalCount - 1);
        state.selectedIds = state.selectedIds.filter(x => x !== id);
        if (state.selectedPatientId === id) {
          state.selectedPatientId = null;
          state.selectedPatient = null;
          state.selectedPatientError = null;
          state.showDetail = false;
        }
      });
  },
});

export const {
  setPatients,
  updatePatientInList,
  removePatientFromList,
  setSearch,
  setStatusFilter,
  setTagFilter,
  setSortBy,
  setUnreadOnly,
  resetFilters,
  setCurrentPage,
  setPageSize,
  setSelectedPatientId,
  setSelectedPatient,
  clearSelectedPatient,
  setActiveTab,
  setShowDetail,
  setShowMessagesPanel,
  toggleSelectedId,
  clearSelectedIds,
  selectAllOnPage,
  toggleFavorite,
  setListPanelWidth,
  setMessagesPanelWidth,
} = crmPatientsSlice.actions;

export default crmPatientsSlice.reducer;
